// Předvyplnění prázdných polí formuláře z akvizičního reportu (object_acquisition).
// Už vyplněná hodnota (uživatel nebo předchozí uložení) se nepřepisuje.

import { horizontalniPropojFromTechReseni } from '../constants/acquisition-report.constants';

/** Sloupec + raw_data reportu navázaného na budovu. */
export interface AcquisitionReportSource {
  statusAcq?: string | null;
  souhlasSmlouva?: string | null;
  hpCount?: number | null;
  bjCount?: number | null;
  floorCount?: number | null;
  techReseniBd?: string | null;
  cisloPopisne?: string | null;
  parcelNumber?: string | null;
  ropId?: string | null;
  ulice?: string | null;
  mesto?: string | null;
  rawData?: Record<string, unknown> | null;
  /** Klíče, které uživatel uložil. Re-import je nesmí přepsat a detail je nesmí doplnit z reportu. */
  userEditedFields?: readonly string[] | null;
}

/** Hodnoty reportu: sloupec má přednost, raw_data doplní prázdné. */
export interface AcquisitionReportValues {
  statusAcq: string | null;
  souhlasSmlouva: string | null;
  hpCount: number | null;
  bjCount: number | null;
  floorCount: number | null;
  komerceCount: number | null;
  techReseniBd: string | null;
  cisloPopisne: string | null;
  parcelNumber: string | null;
  ropId: string | null;
  ulice: string | null;
  mesto: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  typDomu: string | null;
  zpusobPripojeni: string | null;
}

export interface AcquisitionEntryDraft {
  statusAcq?: string | null;
  souhlasSmlouva?: string | null;
  hpCount?: number | null;
  bjCount?: number | null;
  floorCount?: number | null;
  techReseniBd?: string | null;
  cisloPopisne?: string | null;
  parcelNumber?: string | null;
  ropId?: string | null;
  ulice?: string | null;
  mesto?: string | null;
}

export function isBlankText(value: unknown): boolean {
  return typeof value !== 'string' || value.trim() === '';
}

function rawOf(source: AcquisitionReportSource | null | undefined): Record<string, unknown> {
  const raw = source?.rawData;
  return raw && typeof raw === 'object' ? raw : {};
}

function textFromUnknown(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function numberFromUnknown(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value !== 'string') return null;
  const cleaned = value.replace(/\s+/g, '').replace(',', '.');
  if (cleaned === '') return null;
  const n = Number.parseInt(cleaned, 10);
  return Number.isFinite(n) ? n : null;
}

/** Neprázdný text nechá, prázdný doplní z reportu. */
export function prefillText(current: string | null | undefined, fromReport: string | null): string | null {
  if (!isBlankText(current)) return current ?? null;
  return fromReport;
}

/** Číslo včetně 0 je uložená hodnota. null/undefined se doplní z reportu. */
export function prefillNumber(current: number | null | undefined, fromReport: number | null): number | null {
  if (typeof current === 'number' && Number.isFinite(current)) return current;
  return fromReport;
}

function pickText(
  column: unknown,
  ...rawValues: unknown[]
): string | null {
  const fromColumn = textFromUnknown(column);
  if (fromColumn) return fromColumn;
  for (const value of rawValues) {
    const text = textFromUnknown(value);
    if (text) return text;
  }
  return null;
}

function pickNumber(column: unknown, rawValue: unknown): number | null {
  if (typeof column === 'number' && Number.isFinite(column)) return Math.trunc(column);
  const fromColumnText = numberFromUnknown(column);
  if (fromColumnText != null && !isBlankText(column)) return fromColumnText;
  return numberFromUnknown(rawValue);
}

/** Kanonické hodnoty reportu pro předvyplnění formulářů. */
export function readAcquisitionReportFields(
  source: AcquisitionReportSource | null | undefined,
): AcquisitionReportValues {
  const raw = rawOf(source);
  return {
    statusAcq: pickText(source?.statusAcq, raw['statusAcq']),
    souhlasSmlouva: pickText(source?.souhlasSmlouva, raw['souhlasSmlouva']),
    hpCount: pickNumber(source?.hpCount, raw['hpCount']),
    bjCount: pickNumber(source?.bjCount, raw['bjCount']),
    floorCount: pickNumber(source?.floorCount, raw['floorCount']),
    komerceCount: pickNumber(undefined, raw['komerceCount']),
    techReseniBd: pickText(source?.techReseniBd, raw['techReseniBd']),
    cisloPopisne: pickText(source?.cisloPopisne, raw['cisloPopisne']),
    parcelNumber: pickText(source?.parcelNumber, raw['parcelNumber']),
    ropId: pickText(source?.ropId, raw['ropId']),
    ulice: pickText(source?.ulice, raw['ulice']),
    mesto: pickText(source?.mesto, raw['mesto'], raw['obec']),
    contactEmail: pickText(undefined, raw['contactEmail']),
    contactPhone: pickText(undefined, raw['contactPhone']),
    typDomu: pickText(undefined, raw['typDomu']),
    zpusobPripojeni: pickText(undefined, raw['zpusobPripojeni']),
  };
}

const COLUMN_NUMBER_KEYS = ['hpCount', 'bjCount', 'floorCount'] as const;
const COLUMN_TEXT_KEYS = [
  'statusAcq',
  'souhlasSmlouva',
  'techReseniBd',
  'cisloPopisne',
  'parcelNumber',
  'ropId',
  'ulice',
  'mesto',
] as const;

/**
 * Co se má ukázat. Klíč v `userEditedFields` bere jen uloženou hodnotu (i prázdnou).
 * Neuložený klíč bere sloupec, a když je prázdný, hodnotu z importu.
 */
export function readAcquisitionDisplayFields(
  source: AcquisitionReportSource | null | undefined,
): AcquisitionReportValues {
  const report = readAcquisitionReportFields(source);
  const edited = new Set(source?.userEditedFields ?? []);
  if (!source || edited.size === 0) return report;
  const out: AcquisitionReportValues = { ...report };
  const raw = rawOf(source);
  for (const key of COLUMN_NUMBER_KEYS) {
    if (!edited.has(key)) continue;
    const column = source[key];
    out[key] = typeof column === 'number' && Number.isFinite(column) ? Math.trunc(column) : null;
  }
  for (const key of COLUMN_TEXT_KEYS) {
    if (!edited.has(key)) continue;
    out[key] = textFromUnknown(source[key]);
  }
  if (edited.has('komerceCount')) out.komerceCount = numberFromUnknown(raw['komerceCount']);
  if (edited.has('contactEmail')) out.contactEmail = textFromUnknown(raw['contactEmail']);
  if (edited.has('contactPhone')) out.contactPhone = textFromUnknown(raw['contactPhone']);
  if (edited.has('typDomu')) out.typDomu = textFromUnknown(raw['typDomu']);
  if (edited.has('zpusobPripojeni')) out.zpusobPripojeni = textFromUnknown(raw['zpusobPripojeni']);
  return out;
}

/**
 * Prázdná pole akvizičního editoru doplní z reportu.
 * Ručně uložené pole (i prázdné) se z importu nedoplňuje.
 * Technické řešení je zde volný text stejného katalogu — bez převodu na enum.
 */
export function prefillAcquisitionEntryDraft(
  current: AcquisitionEntryDraft,
  source: AcquisitionReportSource | null | undefined,
): AcquisitionEntryDraft {
  const report = readAcquisitionDisplayFields(source);
  return {
    statusAcq: prefillText(current.statusAcq, report.statusAcq),
    souhlasSmlouva: prefillText(current.souhlasSmlouva, report.souhlasSmlouva),
    hpCount: prefillNumber(current.hpCount, report.hpCount),
    bjCount: prefillNumber(current.bjCount, report.bjCount),
    floorCount: prefillNumber(current.floorCount, report.floorCount),
    techReseniBd: prefillText(current.techReseniBd, report.techReseniBd),
    cisloPopisne: prefillText(current.cisloPopisne, report.cisloPopisne),
    parcelNumber: prefillText(current.parcelNumber, report.parcelNumber),
    ropId: prefillText(current.ropId, report.ropId),
    ulice: prefillText(current.ulice, report.ulice),
    mesto: prefillText(current.mesto, report.mesto),
  };
}

/**
 * HP ve specifikaci už uživatel uložil.
 * HP 0 bez pater a bez typu kabeláže je placeholder „nezadáno“, ne ruční nula.
 */
export function isSpecHpUserEntered(spec: {
  hpCountTotal?: number | null;
  floors?: unknown[] | null;
  horizontalniPropoj?: boolean | null;
} | null | undefined): boolean {
  if (!spec) return false;
  const hp = spec.hpCountTotal;
  if (hp == null || !Number.isInteger(hp) || hp < 0) return false;
  if (hp > 0) return true;
  if ((spec.floors?.length ?? 0) > 0) return true;
  return spec.horizontalniPropoj === true || spec.horizontalniPropoj === false;
}

/**
 * Počet bytů na každé patro. Patra = floorCount (jinak 1).
 * Když je BJ víc než pater, byty se rozloží. Méně BJ než pater nechá 1 byt na patro.
 */
export function acquisitionPrefillFloorFlatCounts(
  floorCount: number | null,
  bjCount: number | null,
): number[] {
  const floors = floorCount != null && floorCount > 0 ? Math.floor(floorCount) : 1;
  const counts = Array.from({ length: floors }, () => 1);
  const bj = bjCount != null && bjCount > 0 ? Math.floor(bjCount) : floors;
  let extra = bj - floors;
  let index = 0;
  while (extra > 0) {
    counts[index % floors] += 1;
    extra -= 1;
    index += 1;
  }
  return counts;
}

/**
 * Typ kabeláže ve specifikaci (enum). Neznámý text reportu → null, ne volný řetězec.
 * true/false už ve spec se nemění.
 */
export function prefillSpecCableType(
  current: boolean | null | undefined,
  techReseniBd: string | null | undefined,
): boolean | null {
  if (current === true || current === false) return current;
  return horizontalniPropojFromTechReseni(techReseniBd);
}

export function prefillOwnerContacts<T extends { email?: string | null; telefon?: string | null }>(
  owners: T[],
  source: AcquisitionReportSource | null | undefined,
): T[] {
  const report = readAcquisitionReportFields(source);
  let emailUsed = owners.some((owner) => !isBlankText(owner.email));
  let phoneUsed = owners.some((owner) => !isBlankText(owner.telefon));
  return owners.map((owner) => {
    let email = owner.email ?? '';
    let telefon = owner.telefon ?? '';
    if (!emailUsed && isBlankText(email) && report.contactEmail) {
      email = report.contactEmail;
      emailUsed = true;
    }
    if (!phoneUsed && isBlankText(telefon) && report.contactPhone) {
      telefon = report.contactPhone;
      phoneUsed = true;
    }
    if (email === (owner.email ?? '') && telefon === (owner.telefon ?? '')) return owner;
    return { ...owner, email, telefon };
  });
}

export function prefillParcelAddresses<T extends {
  parcelniCislo?: string | null;
  ulice?: string | null;
  obec?: string | null;
  popisneCislo?: string | null;
}>(
  parcels: T[],
  source: AcquisitionReportSource | null | undefined,
): T[] {
  const report = readAcquisitionReportFields(source);
  return parcels.map((parcel) => {
    const parcelniCislo = prefillText(parcel.parcelniCislo, report.parcelNumber) ?? parcel.parcelniCislo ?? '';
    const ulice = prefillText(parcel.ulice, report.ulice);
    const obec = prefillText(parcel.obec, report.mesto);
    const popisneCislo = prefillText(parcel.popisneCislo, report.cisloPopisne);
    if (
      parcelniCislo === (parcel.parcelniCislo ?? '') &&
      ulice === (parcel.ulice ?? null) &&
      obec === (parcel.obec ?? null) &&
      popisneCislo === (parcel.popisneCislo ?? null)
    ) {
      return parcel;
    }
    return { ...parcel, parcelniCislo, ulice, obec, popisneCislo };
  });
}

export function prefillAcquisitionPartiesFromReport<
  TOwner extends { email?: string | null; telefon?: string | null },
  TParcel extends {
    parcelniCislo?: string | null;
    ulice?: string | null;
    obec?: string | null;
    popisneCislo?: string | null;
  },
>(
  parties: { owners?: TOwner[] | null; parcels?: TParcel[] | null },
  source: AcquisitionReportSource | null | undefined,
): { owners: TOwner[]; parcels: TParcel[] } {
  return {
    owners: prefillOwnerContacts(parties.owners ?? [], source),
    parcels: prefillParcelAddresses(parties.parcels ?? [], source),
  };
}
